// app/public/channel/[referralCode]/page.tsx

import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// import PageTitle from "../../components/ui/PageTitle";
import LoadingIndicator from "../../components/ui/LoadingIndicator";
import ErrorMessage from "../../components/ui/ErrorMessage";
import Button from "../../components/ui/Button";
import KycPromptModal from "../../components/user/KycPromptModal"; // Import modal
import {
  devBypassAgreement,
  getPublicChannelByReferralCode,
  initiateSubscribe,
  previewCoupon,
  verifyTransactionPayment,
} from "../../lib/apiClient"; // New API client function
import {
  CouponPreviewResponse,
  PopulatedChannel,
  Plan,
  InitiateTransactionOrderResponse,
} from "../../types"; // Use PopulatedChannel
import { getErrorMessage, loadRazorpayScript } from "../../lib/utils";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faCheckCircle,
  faStar,
  faUsers,
  faCrown
} from "@fortawesome/free-solid-svg-icons";
import {
  faHeart,
  faEye,
  faComments
} from "@fortawesome/free-regular-svg-icons";
import { useAuth } from "../../context/AuthContext"; // Get auth state
import { ROUTES } from "../../lib/constants";
import { clearAuthData, storeRedirectUrl } from "../../lib/auth"; // Store redirect URL
import FaqSection from "../../components/ui/FaQList";
import PlanCard from "../../components/user/plansCard";

const getPlatformBadge = (platform?: string) => {
  if (platform === "telegram") {
    return { label: "Telegram", className: "bg-sky-500/15 text-sky-300 border-sky-500/40" };
  }
  if (platform === "discord") {
    return { label: "Discord", className: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40" };
  }
  if (platform === "slack") {
    return { label: "Slack", className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" };
  }
  return { label: "Platform", className: "bg-dark-primary text-text-secondary border-dark-border" };
};

// Declare Razorpay type globally if not already in user/my-groups/page.tsx
// declare global { interface Window { Razorpay: any; } }

export default function PublicChannelPage() {
  const router = useNavigate();

  const location = useLocation();
  const params = useParams();
  const searchParams = new URLSearchParams(location.search);
  const referralCode = params.referralCode as string;
  const copCode = searchParams.get("couponCode") || "";

  const { isAuthenticated, user } = useAuth(); // Get auth state and user details

  const [channel, setChannel] = useState<PopulatedChannel | null>(null); // Store fetched channel with populated plans
  const [isLoading, setIsLoading] = useState(true); // Loading state for initial fetch
  const [error, setError] = useState<string | null>(null); // Error fetching data
  const [subActionLoading, setSubActionLoadingState] = useState<{ [subId: string]: boolean }>(
    {}
  );

  const setSubActionLoading = (subId: string, isLoading: boolean) => {
    setSubActionLoadingState((prev) => ({ ...prev, [subId]: isLoading }));
  };
  const couponCode = copCode.trim();
  const [couponInput, setCouponInput] = useState(couponCode);
  const [appliedCouponCode, setAppliedCouponCode] = useState(couponCode);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [planPricingPreviews, setPlanPricingPreviews] = useState<Record<string, CouponPreviewResponse>>({});

  const [isKycModalOpen, setIsKycModalOpen] = useState(false); // State for KYC modal
  const isLocalNoPaymentMode =
    process.env.REACT_APP_LOCAL_BYPASS_MODE === "true";
  const initiateRazorpayPayment = useCallback(
    (orderData: InitiateTransactionOrderResponse) => {
      const subId = "";
      if (!window.Razorpay) {
        toast.error("Payment gateway not loaded. Please refresh.", {
          id: "rzp-sdk-error",
        });
        setSubActionLoading(subId, false);
        return;
      }

      const options = {
        key: orderData.razorpayKeyId,
        amount: orderData.amount, // Amount in smallest currency unit (e.g., paise)
        currency: orderData.currency,
        name: "Telegram-WA Automation", // Replace with your actual company name
        description: `Subscription for ${channel?.name}`,
        order_id: orderData.orderId,
        handler: async (response: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
        }) => {
          // Payment successful according to Razorpay UI
          toast.loading("Verifying payment...", { id: "payment-verify" });
          setSubActionLoading(subId, true); // Keep loading during verification

          try {
            await verifyTransactionPayment(response);
            toast.success("Payment successful! Please complete the next steps", {
              id: "payment-verify",
            });
            setTimeout(() => {
              router(ROUTES.USER_DASHBOARD, { replace: true });
            }, 2000);
          } catch (verifyError) {
            const message = getErrorMessage(verifyError);
            console.error("Payment Verification Failed:", message);
            toast.error(
              `Payment verification failed: ${message}. Please contact support if payment was deducted.`,
              { id: "payment-verify", duration: 7000 }
            );
            // Optionally fetch subscriptions even on verification error, backend might have updated via webhook
            // fetchSubscriptions();
          } finally {
            setSubActionLoading(subId, false);
          }
        },
        prefill: {
          // Optional: Prefill user details
          name: "", // Add user name if available
          email: "", // Add user email if available
          contact: user?.phone || "", // Use phone from context if available
        },
        notes: {
          action: "New Sub",
        },
        theme: {
          color: "#f59e0b", // Example: Blue theme color
        },
        modal: {
          ondismiss: () => {
            // Handle user closing the modal
            console.log("Razorpay checkout modal dismissed by user");
            toast.error("Payment cancelled.", { id: "payment-dismissed" });
            setSubActionLoading(subId, false); // Reset loading state
          },
        },
      };

      const rzp = new window.Razorpay(options);

      // Handle payment failure callback from Razorpay
      rzp.on(
        "payment.failed",
        (response: {
          error: {
            code: string;
            description: string;
            source: string;
            step: string;
            reason: string;
            metadata: { order_id: string; payment_id: string };
          };
        }) => {
          console.error("Razorpay Payment Failed:", response.error);
          toast.error(
            `Payment Failed: ${response.error.description || response.error.reason
            }`,
            { id: "payment-failed", duration: 7000 }
          );
          setSubActionLoading(subId, false);
          // Refresh list in case status changed via webhook anyway
          // fetchSubscriptions();
        }
      );

      // Open the Razorpay checkout modal
      rzp.open();
    },
    [user]
  ); // Dependencies for the callback
  const handleSub = async (plan: Plan) => {
    const planID = plan._id;
    setSubActionLoading(planID, true);

    toast.loading("Creating subscription...", { id: `new-${planID}` });

    try {
      if (isLocalNoPaymentMode) {
        const bypassResult = await devBypassAgreement({
          plan_id: planID,
          couponCode: appliedCouponCode || undefined,
          referralCode: referralCode,
          isCombo: false,
        });

        toast.dismiss(`new-${planID}`);

        toast.success("Subscription created. Complete KYC if required to continue.");
        setTimeout(() => {
          router(ROUTES.USER_DASHBOARD, { replace: true });
        }, 1200);
        setSubActionLoading(planID, false);
        return;
      }

      const orderDetails = await initiateSubscribe({
        plan_id: planID,
        couponCode: appliedCouponCode || undefined,
        referralCode: referralCode,
        isCombo: false,
      });

      toast.dismiss(`new-${planID}`);

      // Free plan — already activated, no payment needed
      if (orderDetails.freeActivated) {
        toast.success("Subscribed successfully!", { id: `new-${planID}` });
        setTimeout(() => {
          router(ROUTES.USER_DASHBOARD, { replace: true });
        }, 1500);
        setSubActionLoading(planID, false);
        return;
      }

      // Paid plan — open Razorpay checkout
      initiateRazorpayPayment(orderDetails);
      // Loading state is now managed by Razorpay callbacks
    } catch (err) {
      const message = getErrorMessage(err);
      console.error(`New Sub failed for plan ${planID}:`, message);
      toast.error(`New Sub failed: ${message}`, { id: `new-${planID}` });
      setSubActionLoading(planID, false);
    }
  };

  const applyCouponToPlans = useCallback(async () => {
    const nextCode = couponInput.trim().toUpperCase();
    const activePlans = channel?.associated_plan_ids?.filter((plan) => plan.is_active) || [];

    if (!nextCode) {
      setAppliedCouponCode("");
      setCouponError(null);
      setPlanPricingPreviews({});
      return;
    }

    if (!activePlans.length) {
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError(null);
    try {
      const previews = await Promise.all(
        activePlans.map(async (plan) => {
          const pricing = await previewCoupon({
            couponCode: nextCode,
            plan_id: plan._id,
            isCombo: false,
            condition: "first_time",
          });
          return [plan._id, pricing] as const;
        })
      );

      setPlanPricingPreviews(Object.fromEntries(previews));
      setAppliedCouponCode(nextCode);
      toast.success("Coupon applied.");
    } catch (err) {
      const message = getErrorMessage(err);
      setCouponError(message);
      setAppliedCouponCode("");
      setPlanPricingPreviews({});
      toast.error(message);
    } finally {
      setIsApplyingCoupon(false);
    }
  }, [channel?.associated_plan_ids, couponInput]);
  // Memoized function to fetch channel and plans
  const fetchChannelAndPlans = useCallback(async () => {
    if (!referralCode) {
      setError("Invalid channel link.");
      setIsLoading(false);
      return;
    }
    console.log(`Fetching public channel details for code: ${referralCode}`);
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPublicChannelByReferralCode(referralCode);
      setChannel(data);

    } catch (err) {
      const message = getErrorMessage(err);
      console.error(`Failed to fetch public channel ${referralCode}:`, message);
      setError(message);
      toast.error(`Could not load channel: ${message}`, {
        id: `fetch-public-channel-${referralCode}-error`,
      });
    } finally {
      setIsLoading(false);
    }
  }, [referralCode]); // Dependency: referralCode

  // Fetch data on component mount
  useEffect(() => {
    if (isAuthenticated && user?.role && user.role !== "User") {
      const redirectPath = `${location.pathname}${location.search}`;
      clearAuthData();
      storeRedirectUrl(redirectPath);
      toast("Please continue with a User account for enrollment.");
      router(ROUTES.LOGIN_USER, { replace: true, state: { from: redirectPath } });
      return;
    }

    fetchChannelAndPlans();
    if (!isLocalNoPaymentMode) {
      loadRazorpayScript().then((loaded) => {
        if (!loaded) {
          toast.error("Failed to load payment gateway script.", {
            id: "razorpay-public-load-error",
          });
        }
      });
    }
  }, [fetchChannelAndPlans, isAuthenticated, isLocalNoPaymentMode, location.pathname, location.search, router, user?.role]); // Run only once on mount

  useEffect(() => {
    if (couponCode && channel?.associated_plan_ids?.length) {
      applyCouponToPlans();
    }
  }, [applyCouponToPlans, channel?.associated_plan_ids?.length, couponCode]);

  // --- Subscribe Button Handler ---
  const handleSubscribeClick = (plan: Plan) => {
    console.log(
      `Subscribe clicked for plan ${plan.name} on channel ${channel?.name}`
    );

    if (!isAuthenticated) {
      console.log("User not authenticated, redirecting to login.");
      // Store the current URL before redirecting
      const redirectPath = `${location.pathname}${location.search}`;
      storeRedirectUrl(redirectPath);
      // Redirect to user login page
      router(ROUTES.LOGIN_USER, { state: { from: redirectPath } });
      return; // Stop here
    }

    if (channel.is_post) {
      handleSub(plan);
    } else if (!channel.kycEnabled || user.isKycSubmitted) {
      // KYC not required (disabled) OR already submitted — proceed to payment
      console.log(
        `User authenticated and KYC check passed. Proceeding to payment for Plan ID: ${plan._id}, Channel ID: ${channel?._id}`
      );
      handleSub(plan);
    }
    else {
      setIsKycModalOpen(true)
    }
  };

  // --- Render Logic ---
  const renderContent = () => {
    if (isLoading) {
      return <LoadingIndicator text="Loading channel and plans..." />;
    }

    if (error) {
      return (
        <div className="text-center">
          <ErrorMessage title="Error Loading Channel" message={error} />
          <Button onClick={fetchChannelAndPlans} className="mt-4">
            Retry
          </Button>
        </div>
      );
    }

    if (!channel) {
      // Should be covered by error state, but as a safeguard
      return (
        <ErrorMessage title="Error" message="Channel data not available." />
      );
    }

    const activePlans =
      channel.associated_plan_ids?.filter((plan) => plan.is_active) || [];
    const platformBadge = getPlatformBadge(channel.platform);

    return (
      <div className="space-y-8">
        {/* Hero Channel Card */}
        <div className="relative bg-gradient-to-br from-dark-secondary via-dark-tertiary to-dark-secondary p-8 rounded-2xl shadow-2xl border border-dark-border overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 bg-gradient-to-r from-golden-accent/20 to-transparent"></div>
          </div>
          
          <div className="relative z-10">
            {/* Branding strip */}
            {(channel.branding?.name || channel.branding?.logoUrl) && (
              <div className="flex items-center gap-2 mb-4">
                {channel.branding.logoUrl && (
                  <img src={channel.branding.logoUrl} alt={channel.branding.name || "Brand"} className="w-6 h-6 rounded object-contain" />
                )}
                {channel.branding.name && (
                  <span className="text-xs text-text-muted font-medium tracking-wide">{channel.branding.name}</span>
                )}
                {channel.branding.tagline && (
                  <span className="text-xs text-text-muted/60">— {channel.branding.tagline}</span>
                )}
              </div>
            )}
            {!(channel.branding?.name || channel.branding?.logoUrl) && (
              <div className="flex items-center gap-2 mb-4">
                <img src="/G1.svg" alt="SuBs" className="w-5 h-5 rounded" />
                <span className="text-xs text-text-muted font-medium tracking-wide">SuBs</span>
              </div>
            )}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-golden-accent to-golden-accent/80 rounded-2xl flex items-center justify-center shadow-lg">
                  <FontAwesomeIcon icon={faPlay} className="text-2xl text-black" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-text-primary font-montserrat mb-2">
                    {channel.name}
                  </h1>
                  <div className="flex items-center space-x-4 text-sm text-text-secondary">
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faUsers} className="text-golden-accent" />
                      <span>Premium Channel</span>
                    </div>
                      <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${platformBadge.className}`}>
                        {platformBadge.label}
                      </span>
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faStar} className="text-golden-accent" />
                      <span>Verified</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-4 py-2 bg-golden-accent/10 rounded-full border border-golden-accent/20">
                <span className="text-golden-accent font-medium text-sm">Active</span>
              </div>
            </div>
            
            <p className="text-text-secondary leading-relaxed mb-6">
              {channel.description}
            </p>
            
            {/* Channel Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-dark-primary/50 rounded-xl border border-dark-border/50">
                <FontAwesomeIcon icon={faEye} className="text-golden-accent text-xl mb-2" />
                <div className="text-text-primary font-semibold">Premium</div>
                <div className="text-xs text-text-secondary">Content</div>
              </div>
              <div className="text-center p-4 bg-dark-primary/50 rounded-xl border border-dark-border/50">
                <FontAwesomeIcon icon={faComments} className="text-golden-accent text-xl mb-2" />
                <div className="text-text-primary font-semibold">24/7</div>
                <div className="text-xs text-text-secondary">Support</div>
              </div>
              <div className="text-center p-4 bg-dark-primary/50 rounded-xl border border-dark-border/50">
                <FontAwesomeIcon icon={faHeart} className="text-golden-accent text-xl mb-2" />
                <div className="text-text-primary font-semibold">Exclusive</div>
                <div className="text-xs text-text-secondary">Access</div>
              </div>
            </div>
          </div>
        </div>

        {/* Plans Section */}
        <div className="bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-primary p-8 rounded-2xl shadow-2xl border border-dark-border">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-text-primary font-montserrat mb-2">
                Choose Your Plan
              </h2>
              <p className="text-text-secondary">Select the perfect subscription plan for your needs</p>
            </div>
            <div className="flex items-center space-x-2 px-4 py-2 bg-functional-success/10 rounded-full border border-functional-success/20">
              <FontAwesomeIcon icon={faCheckCircle} className="text-functional-success" />
              <span className="text-functional-success font-medium text-sm">Instant Access</span>
            </div>
          </div>

          {/*
          <div className="mb-6 rounded-xl border border-dark-border/60 bg-dark-primary/40 p-4">
            <div className="flex items-end gap-0">
              <div className="flex-1">
                <label htmlFor="couponCode" className="mb-1 block text-xs font-medium text-text-secondary">
                  Coupon Code
                </label>
                <div className="flex items-stretch">
                  <input
                    id="couponCode"
                    name="couponCode"
                    type="text"
                    value={couponInput}
                    onChange={(e) => setCouponInput(String(e.target.value || "").toUpperCase())}
                    placeholder="ENTERCOUPON"
                    className="h-11 w-full rounded-l-md rounded-r-none border border-dark-border border-r-0 bg-dark-tertiary px-3 text-sm text-text-primary placeholder:text-text-secondary/70 focus:outline-none focus:ring-2 focus:ring-golden-focus-ring"
                  />
                  <Button
                    onClick={applyCouponToPlans}
                    isLoading={isApplyingCoupon}
                    className="h-11 rounded-l-none rounded-r-md px-5 whitespace-nowrap"
                  >
                    Apply Coupon
                  </Button>
                </div>
              </div>
            </div>
            {couponError ? <p className="mt-1 text-xs text-functional-danger">{couponError}</p> : null}
            {appliedCouponCode && !couponError ? (
              <p className="text-sm text-golden-accent mt-2">
                Coupon <strong>{appliedCouponCode}</strong> applied. Discounted prices are shown below.
              </p>
            ) : null}
          </div>
          */}

          {activePlans.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-dark-tertiary rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faCrown} className="text-3xl text-text-secondary" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-2">No Plans Available</h3>
              <p className="text-text-secondary">No active plans available for this channel currently.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {activePlans.map((plan) => (
                <PlanCard
                  key={plan._id}
                  plan={plan}
                  handleSubscribeClick={handleSubscribeClick}
                  couponCode={appliedCouponCode || undefined}
                  platform={channel.platform}
                  pricingPreview={planPricingPreviews[plan._id]}
                  actionLabel={
                    isLocalNoPaymentMode
                      ? "Start KYC Verification"
                      : "Subscribe"
                  }
                  showPricing={!isLocalNoPaymentMode}
                  isSubmitting={!!subActionLoading[plan._id]}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-primary">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,188,6,0.1),transparent_50%)]" />
      </div>
      
      <div className="relative z-10 flex justify-center items-start p-6">
        <div className="w-full max-w-4xl">
          <main className="container mx-auto py-8 md:py-12">
            {/* Enhanced Page Title */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-golden-accent rounded-xl flex items-center justify-center">
                  <FontAwesomeIcon icon={faPlay} className="text-xl text-black" />
                </div>
                <h1 className="text-4xl font-bold text-text-primary font-montserrat">
                  Channel & Plans
                </h1>
              </div>
              <p className="text-text-secondary text-lg max-w-2xl mx-auto">
                {channel?.name
                  ? `Discover premium content and exclusive access to ${channel.name}`
                  : "Loading channel details..."}
              </p>
            </div>
            
            {renderContent()}
          </main>

          {/* Render the KYC Prompt Modal conditionally */}
          {user && (
            <KycPromptModal
              isOpen={isKycModalOpen}
              onClose={() => setIsKycModalOpen(false)}
              sub={null}
            />
          )}

          <FaqSection mode={isLocalNoPaymentMode ? "verification" : "default"} />
        </div>
      </div>
    </div>
  );
}
